import posthog from "./posthog";
import { EVENTS } from "./events";

/**
 * Normal login. Call immediately after dispatch(setLoginToken(response)) in
 * component/login/login.js — NOT from handleGetFirstTierMenu, which is delayed 5s by
 * setTimeout there.
 *
 * Destructure explicitly: responseData also carries userToken.
 */
export function identifyNormalUser(responseData) {
  if (!responseData || !responseData.dealerId) return;

  const {
    dealerId,
    dealerCompany,
    dealerMemberTypeName,
    userEmail,
    userName,
    masterRoleName,
  } = responseData;

  posthog.reset(); // never merge identities
  posthog.identify(`dealer_${dealerId}`, {
    dealer_id: dealerId,
    company_name: dealerCompany,
    member_type: dealerMemberTypeName, // Admin|Combined|Retailer|Vendor|VendorDiamond
    email: userEmail,
    user_name: userName,
    role: masterRoleName,
    is_impersonated: false,
  });

  // Single group type — Combined doesn't map onto vendor/retailer/dealer.
  posthog.group("company", String(dealerId), {
    name: dealerCompany,
    member_type: dealerMemberTypeName,
  });

  posthog.register({
    dealer_id: dealerId,
    member_type: dealerMemberTypeName,
    company_name: dealerCompany,
    is_impersonated: false,
  });

  posthog.capture(EVENTS.USER_LOGGED_IN, { member_type: dealerMemberTypeName });
}

/**
 * Impersonation target tab. Call from the App.js handoff success block, after
 * dispatch(setImpersonateHeaderMenuData(...)).
 */
export function identifyImpersonatedUser(handoff, adminSession, sessionRef) {
  const target = handoff.loginData || {};
  const admin = (adminSession && adminSession.adminData) || {};

  posthog.reset(); // MANDATORY — PostHog silently no-ops identify() onto an
  // already-identified distinct_id otherwise.
  posthog.identify(`dealer_${handoff.dealerId}`, {
    dealer_id: handoff.dealerId,
    company_name: target.dealerCompany,
    member_type: target.dealerMemberTypeName,
    email: target.userEmail,
    user_name: target.userName,
    is_impersonated: true,
    acting_admin_id: admin.dealerId,
    acting_admin_email: admin.userEmail,
  });
  posthog.group("company", String(handoff.dealerId), {
    name: target.dealerCompany,
  });
  posthog.register({
    dealer_id: handoff.dealerId,
    company_name: target.dealerCompany,
    member_type: target.dealerMemberTypeName,
    is_impersonated: true,
    acting_admin_id: admin.dealerId,
    impersonation_session_ref: sessionRef,
  });

  posthog.startSessionRecording(); // impersonation is always recorded at 100%
  posthog.capture(EVENTS.IMPERSONATION_STARTED_TARGET, {
    target_dealer_id: handoff.dealerId,
    session_ref: sessionRef,
    started_at: handoff.impersonationTime,
  });
}

/**
 * Teardown. Emits impersonation_ended when the tab was an impersonation session,
 * otherwise user_logged_out. Reads session type from the session manager rather than
 * PostHog state so it stays correct even if capture fails.
 */
export function resetAnalytics(reason = "logout") {
  let wasImpersonating = false;
  try {
    // eslint-disable-next-line global-require
    wasImpersonating =
      require("../utils/simpleSessionManager").default.getSessionType() ===
      "impersonation";
  } catch (e) {
    // ignore
  }

  try {
    posthog.capture(
      wasImpersonating ? EVENTS.IMPERSONATION_ENDED : EVENTS.USER_LOGGED_OUT,
      { end_reason: reason }
    );
  } catch (e) {
    // never block logout
  }

  try {
    posthog.reset();
  } catch (e) {
    // ignore
  }
}
